import { useState, type InputHTMLAttributes, type ReactNode } from 'react';

import { PASSWORD_RULES } from '@/lib/validation';

import { describedBy } from './a11y';
import { Field } from './field';

interface PasswordFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: ReactNode;
  error?: string | undefined;
  /** Current value, needed only to render the requirements checklist. */
  currentValue?: string;
  showRequirements?: boolean;
}

export function PasswordField({
  id,
  label,
  error,
  currentValue = '',
  showRequirements = false,
  ...input
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const listId = `${id}-requirements`;
  return (
    <Field id={id} label={label} error={error}>
      <div className="input-wrap">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className="input"
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [describedBy(id, false, Boolean(error)), showRequirements ? listId : '']
              .filter(Boolean)
              .join(' ') || undefined
          }
          autoComplete={input.autoComplete ?? 'current-password'}
          {...input}
        />
        <button
          type="button"
          className="input-wrap__button"
          onClick={() => {
            setVisible((v) => !v);
          }}
          aria-pressed={visible}
          title={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
      {showRequirements && (
        <div id={listId}>
          <div className="checklist__title">Password Requirements</div>
          <ul className="checklist">
            {PASSWORD_RULES.map((rule) => (
              <li key={rule.key} data-ok={rule.test(currentValue)}>
                {rule.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Field>
  );
}
